"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiCollectionsClient = void 0;
const ApiSubClient_1 = require("../../common/client/ApiSubClient");
const expandUrlTemplate_1 = __importDefault(require("../../common/utils/expandUrlTemplate"));
const CollectionsConverter_1 = require("../CollectionsConverter");
const CollectionUpdateConflictError_1 = require("./CollectionUpdateConflictError");
class ApiCollectionsClient extends ApiSubClient_1.ApiSubClient {
    static extractIdFromLocation(response) {
        const link = response.headers.location;
        return link.substr(link.lastIndexOf('/') + 1);
    }
    get(id, projection, referer) {
        const collectionLink = this.getLinkOrThrow('collection');
        return this.axios
            .get((0, expandUrlTemplate_1.default)(collectionLink.href, {
            id,
            projection,
            referer,
        }))
            .then((response) => CollectionsConverter_1.CollectionsConverter.convert(response.data));
    }
    getCollections(filter) {
        let filteredCollectionsLink;
        try {
            filteredCollectionsLink = this.getLinkOrThrow('searchAllCollections');
        }
        catch (_ex) {
            filteredCollectionsLink = this.getLinkOrThrow('searchCollections');
        }
        return this.getCollectionsDetailedView(filter, filteredCollectionsLink);
    }
    getMyCollections(filter) {
        const myCollectionsLink = this.getLinkOrThrow('myCollections');
        return this.getCollectionsDetailedView(filter, myCollectionsLink);
    }
    getMyCollectionsWithoutDetails(filter) {
        const myCollectionsLink = this.getLinkOrThrow('myCollections');
        return this.getCollectionsListView(filter, myCollectionsLink);
    }
    getMySavedCollections(filter) {
        const mySavedCollectionsLink = this.getLinkOrThrow('mySavedCollections');
        return this.getCollectionsDetailedView(filter, mySavedCollectionsLink);
    }
    getMySavedCollectionsWithoutDetails(filter) {
        const mySavedCollectionsLink = this.getLinkOrThrow('mySavedCollections');
        return this.getCollectionsListView(filter, mySavedCollectionsLink);
    }
    getMyUserSharedBookmarkedCollections(filter) {
        const userSharedCollections = this.getLinkOrThrow('userSharedBookmarkedCollections');
        return this.getCollectionsListView(filter, userSharedCollections);
    }
    getMyBoclipsBookmarkedCollections(filter) {
        const boclipsCollections = this.getLinkOrThrow('boclipsSharedCollections');
        return this.getCollectionsListView(filter, boclipsCollections);
    }
    getPromotedForCollections(filter) {
        const promotedForCollectionsLink = this.getLinkOrThrow('promotedForCollections');
        return this.axios
            .get((0, expandUrlTemplate_1.default)(promotedForCollectionsLink.href, {
            promotedFor: filter.promotedFor,
        }))
            .then((response) => CollectionsConverter_1.CollectionsConverter.convertListViewPage(response.data).page);
    }
    getMySavedAndEditableCollectionsWithoutDetails(filter) {
        return this.getMySavedCollectionsWithoutDetails({
            ...filter,
            canEdit: true,
        });
    }
    create(request) {
        const createCollectionLink = this.getLinkOrThrow('createCollection');
        return this.axios
            .post(createCollectionLink.href, request)
            .then((response) => ApiCollectionsClient.extractIdFromLocation(response));
    }
    addCommentToCollectionVideo(collection, request) {
        const addCommentLink = collection.links.addComment?.getOriginalLink();
        if (addCommentLink === undefined) {
            throw Error(`add comment link not available for collection ${collection.id}`);
        }
        return this.axios
            .post(addCommentLink, { comment: request })
            .then((response) => CollectionsConverter_1.CollectionsConverter.convert(response.data));
    }
    update(id, request) {
        const updateCollectionLink = this.getLinkOrThrow('collection');
        return this.axios.patch((0, expandUrlTemplate_1.default)(updateCollectionLink.href, { id }), request);
    }
    safeUpdate(collection, request) {
        const updateCollectionLink = collection.links.safeEdit?.getOriginalLink();
        if (updateCollectionLink === undefined) {
            throw Error(`safeUpdate link not available for collection ${collection.id}`);
        }
        return this.axios.patch(updateCollectionLink, request).then(() => { }, (error) => {
            if (error.status === 409) {
                return Promise.reject(new CollectionUpdateConflictError_1.CollectionUpdateConflictError(collection.id));
            }
            else {
                return Promise.reject(error);
            }
        });
    }
    addVideoToCollection(collection, videoId, highlightId = null) {
        const link = collection.links.addVideo;
        if (!link) {
            return Promise.reject(`adding video not available for collection "${collection.id}"`);
        }
        return this.axios.put((0, expandUrlTemplate_1.default)(link.getOriginalLink(), {
            video_id: videoId,
            highlight_id: highlightId,
        }));
    }
    removeVideoFromCollection(collection, videoId, highlightId = null) {
        const link = collection.links.removeVideo;
        if (!link) {
            return Promise.reject(`removing video not available for collection "${collection.id}"`);
        }
        return this.axios.delete((0, expandUrlTemplate_1.default)(link.getOriginalLink(), {
            video_id: videoId,
            highlight_id: highlightId,
        }));
    }
    bookmark(collection) {
        const bookmarkLink = collection.links.bookmark;
        if (!bookmarkLink) {
            return Promise.reject(`Collection ${collection.id} doesn't have a bookmark link`);
        }
        return this.axios
            .patch(bookmarkLink.getOriginalLink())
            .then((response) => CollectionsConverter_1.CollectionsConverter.convert(response.data));
    }
    unbookmark(collection) {
        const unbookmarkLink = collection.links.unbookmark;
        if (!unbookmarkLink) {
            return Promise.reject(`Collection ${collection.id} doesn't have an unbookmark link`);
        }
        return this.axios
            .patch(unbookmarkLink.getOriginalLink())
            .then((response) => CollectionsConverter_1.CollectionsConverter.convert(response.data));
    }
    delete(collection) {
        const link = collection.links.remove;
        if (!link) {
            return Promise.reject(`Deleting collection "${collection.id}" is not available`);
        }
        return this.axios.delete((0, expandUrlTemplate_1.default)(link.getOriginalLink(), { id: collection.id }));
    }
    updatePermission(collection, permissions) {
        const link = collection.links.updatePermissions;
        if (!link) {
            return Promise.reject(`Can't change permissions for collection "${collection.id}"`);
        }
        return this.axios.patch((0, expandUrlTemplate_1.default)(link.getOriginalLink(), { id: collection.id }), permissions);
    }
    removeCommentFromCollectionVideo(collection, commentId) {
        const link = collection.links.removeComment;
        if (!link) {
            return Promise.reject(`Can't remove comment "${commentId}" for collection "${collection.id}"`);
        }
        return this.axios.delete((0, expandUrlTemplate_1.default)(link.getOriginalLink(), {
            id: collection.id,
            comment_id: commentId,
        }));
    }
    getCollectionsListView(filter, link) {
        const { query, partialTitleMatch, page, size, canEdit } = filter;
        return this.axios
            .get((0, expandUrlTemplate_1.default)(link.href, {
            query: query,
            partial_title_match: partialTitleMatch,
            page,
            size,
            projection: 'list',
            can_edit: canEdit,
        }))
            .then((response) => CollectionsConverter_1.CollectionsConverter.convertListViewPage(response.data));
    }
    getCollectionsDetailedView(filter, link) {
        const { query, page, size, ignore_discoverable } = filter;
        return this.axios
            .get((0, expandUrlTemplate_1.default)(link.href, {
            query: query,
            page,
            size,
            projection: 'details',
            ignore_discoverable,
        }))
            .then((response) => CollectionsConverter_1.CollectionsConverter.convertPage(response.data));
    }
}
exports.ApiCollectionsClient = ApiCollectionsClient;
//# sourceMappingURL=ApiCollectionsClient.js.map